/**
 * Environment detection constants and utilities
 * Centralized domain patterns for consistent environment detection across the app
 */

import { logger } from '@/services/LoggingService';

// Development environment domain patterns
export const DEVELOPMENT_DOMAINS = [
  'localhost',
  '.lovable.app',
  '.lovableproject.com',
  'preview--',
  '127.0.0.1'
] as const;

// Production domain patterns
export const PRODUCTION_DOMAINS = [
  'altsiq.ai',
  'superalts.ca',
  'unislumen.com'
] as const;

// Preview environment specific patterns
export const PREVIEW_PATTERNS = [
  'preview--',
  '.lovable.app',
  '.lovableproject.com'
] as const;

export type EnvironmentType = 'development' | 'preview' | 'production';

/**
 * Check if current hostname matches development environment patterns
 */
export function isDevEnvironment(): boolean {
  if (typeof window === 'undefined') return false;
  
  const hostname = window.location.hostname;
  
  return DEVELOPMENT_DOMAINS.some(pattern => {
    if (pattern.startsWith('.')) {
      return hostname.includes(pattern);
    }
    return hostname === pattern || hostname.startsWith(pattern);
  });
}

/**
 * Check if current hostname matches preview environment patterns
 */
export function isPreviewEnvironment(): boolean {
  if (typeof window === 'undefined') return false;
  
  const hostname = window.location.hostname;
  const origin = window.location.origin;
  
  return PREVIEW_PATTERNS.some(pattern => {
    if (pattern.startsWith('.')) {
      return hostname.includes(pattern);
    }
    return origin.includes(pattern) || hostname.includes(pattern);
  });
}

/**
 * Check if current hostname matches production environment patterns
 */
export function isProductionEnvironment(): boolean {
  if (typeof window === 'undefined') return false;
  
  const hostname = window.location.hostname;
  
  return PRODUCTION_DOMAINS.some(domain => 
    hostname === domain || hostname.endsWith(`.${domain}`)
  );
}

/**
 * Get the current environment type based on hostname
 */
export function getCurrentEnvironmentType(): EnvironmentType {
  if (isPreviewEnvironment()) return 'preview';
  if (isProductionEnvironment()) return 'production';
  return 'development';
}

/**
 * Check if storage should be allowed based on environment
 * Development and preview environments are considered safe for storage testing
 */
export function isStorageSafeEnvironment(): boolean {
  const envType = getCurrentEnvironmentType();
  const result = envType === 'development' || envType === 'preview';
  
  logger.debug('environments', 'Storage safety check', {
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'undefined',
    environmentType: envType,
    isStorageSafe: result
  });
  
  return result;
}

/**
 * Get current origin with fallback for SSR
 */
export function getCurrentOrigin(): string {
  if (typeof window !== 'undefined') {
    const { protocol, hostname, port } = window.location;
    if (hostname === 'www.unislumen.com') {
      return `${protocol}//unislumen.com`;
    }
    return window.location.origin;
  }
  
  // Use environment-appropriate fallback for SSR
  const envMode = import.meta.env?.MODE || 'development';
  if (envMode === 'production') return 'https://unislumen.com';
  if (envMode === 'development') return 'http://localhost:8080';
  return 'https://superalts-marketplace.lovable.app';
}

/**
 * Check if we're in any Lovable-hosted environment
 */
export function isLovableHosted(): boolean {
  if (typeof window === 'undefined') return false;
  
  const hostname = window.location.hostname;
  return hostname.includes('.lovable.app') || hostname.includes('.lovableproject.com');
}